import { Injectable } from '@angular/core';
import { defer, Observable } from 'rxjs';
import { LookupModel } from '../../api/models';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class GooglePlacesService {
  private request: any = { input: '', language: 'en-US', region: 'us', includedRegionCodes: ['us'] };
  private newestRequestId = 0;

  constructor() {
    this.loadGoogleMapsScript().then(() => this.refreshToken());
  }

  public getSuggestions(query: string): Observable<LookupModel[]> {
    return defer(() => this.getSuggestionsPromise(query));
  }

  public getPlaceDetails(placeId: string): Observable<google.maps.places.Place> {
    return defer(() => this.getPlaceDetailsPromise(placeId));
  }

  private async getPlaceDetailsPromise(placeId: string): Promise<google.maps.places.Place> {
    const { Place } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;

    const place = new Place({ id: placeId, requestedLanguage: 'en-US' });

    await place.fetchFields({
      fields: ['addressComponents', 'formattedAddress'],
    });

    this.refreshToken(); // refresh token after selection

    return place;
  }

  private async getSuggestionsPromise(query: string): Promise<LookupModel[]> {
    if (!query) {
      return [];
    }

    this.request.input = query;
    const requestId = ++this.newestRequestId;

    const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(this.request);

    if (requestId !== this.newestRequestId) {
      return [];
    }

    return suggestions.map((s: any) => ({
      id: s.placePrediction.placeId,
      text: s.placePrediction.text.toString(),
    }));
  }

  private refreshToken() {
    this.request.sessionToken = new google.maps.places.AutocompleteSessionToken();
  }

  private loadGoogleMapsScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApi}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = err => reject(err);
      document.head.appendChild(script);
    });
  }
}
